import { FC, useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { UserType } from 'api/models';
import { TimedRender } from 'components/atoms/utils/timed-render';
import { useUser } from 'hooks/use-user';
import { IPINFO_URL, RH_EVENT_ORIGIN } from 'routes/vendor/constants';
import {
  CloseButton,
  injectRHWidget,
  IntroModalWidth,
  IntroModalWindow,
  LoadingSpinner,
  modalMaskCSS,
  RevenueHeroState,
  RHContainer,
  RHTitle,
  StyledModal,
} from 'routes/vendor/intro-modal';
import { logger } from 'services';

const RH_TARGET = 'revenuehero_target';

const RhWidget = ({ rhData, close }) => {
  const { rhWidget: hero } = window as unknown as IntroModalWindow;
  const loading = !hero || !rhData;
  const [rhHeight, setRHHeight] = useState(489);
  const [isBooking, setIsBooking] = useState(true);

  useEffect(() => {
    if (loading) {
      return;
    }

    const messageEventCallback = (event: MessageEvent) => {
      if (event.origin !== RH_EVENT_ORIGIN || !event.isTrusted) {
        return;
      }

      if (event.data?.type === 'RESIZE_IFRAME') {
        setRHHeight(event.data.height + 4);
      } else if (event.data?.type === 'MEETING_BOOKED') {
        setIsBooking(false);
      }
    };

    try {
      window.addEventListener('message', messageEventCallback);
      hero.dialog.setEmbedTarget(`#${RH_TARGET}`);
      hero.dialog.open(rhData);
    } catch (e) {
      logger.error('Unable to show Revenue Hero widget', { error: e });
      close();
    }

    // eslint-disable-next-line consistent-return
    return () => {
      window.removeEventListener('message', messageEventCallback);
    };
  }, [close, rhData, loading, hero.dialog]);

  if (loading) {
    return <LoadingSpinner size="large" />;
  }

  return (
    <>
      <RHTitle>
        <span>
          {isBooking && <FormattedMessage id="signup_onboarding_rh" />}
        </span>
        <CloseButton type="text" onClick={close}>
          <FormattedMessage id={isBooking ? 'skip' : 'close'} />
        </CloseButton>
      </RHTitle>
      <RHContainer id={RH_TARGET} height={rhHeight} />
    </>
  );
};

const RhDemoModalContent = ({ rhState, setRhState, onFinish }) => {
  const {
    user: { userType, emailId },
  } = useUser();

  const [rhData, setRhData] = useState(null);

  useEffect(() => {
    const _window = window as unknown as IntroModalWindow;

    if (
      rhState === RevenueHeroState.Loading &&
      userType !== UserType.Partner &&
      (window as unknown as IntroModalWindow).rhWidget === undefined
    ) {
      // Run only once
      injectRHWidget({
        emailId,
        rhState,
        setRhState,
        setRHData: setRhData,
        onFinish,
      });
    } else {
      if (rhData !== null) {
        return;
      }
      (async () => {
        let country = '';

        try {
          const ipResponse = await fetch(IPINFO_URL);
          const ipData = await ipResponse.json();

          country = ipData.country;
        } catch (e) {
          logger.error('Unable to fetch IP address', { error: e });
        }

        _window.rhWidget
          .submit({
            email: emailId,
            ip_detected_country: country,
          })
          .then((data) => {
            if (data) {
              setRhData(data);
              setRhState(RevenueHeroState.Qualified);
            } else if (rhState === RevenueHeroState.Loading) {
              // User was disqualified from the meeting
              setRhState(RevenueHeroState.Disqualified);
            } else {
              // User was disqualified and the widget is being shown currently, so close it
              onFinish();
            }
          });
      })();
    }
  }, [emailId, rhState, setRhState, setRhData, onFinish, userType, rhData]);
  if (rhState === RevenueHeroState.Qualified) {
    return (
      <TimedRender
        hideAfterMs={2_500}
        retainIf={() => rhData !== null}
        onHide={() => {
          logger.warn('Revenue Hero widget did not load in time');
          onFinish();
        }}
      >
        <RhWidget rhData={rhData} close={onFinish} />
      </TimedRender>
    );
  }

  return null;
};

export const RhModal: FC<{
  visible: boolean;
  close: () => void;
}> = ({ visible, close }: { visible: boolean; close: () => void }) => {
  const [rhState, setRhState] = useState<RevenueHeroState>(
    RevenueHeroState.Loading,
  );

  if (!visible) {
    return null;
  }

  return (
    <StyledModal
      width={
        rhState === RevenueHeroState.Qualified
          ? IntroModalWidth.RevenueHeroWidget
          : 0
      }
      open={visible}
      closable={false}
      footer={null}
      centered
      maskStyle={modalMaskCSS}
      zIndex={1012}
      destroyOnClose
    >
      <RhDemoModalContent
        rhState={rhState}
        setRhState={setRhState}
        onFinish={close}
      />
    </StyledModal>
  );
};
